import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';

import {
    BooleanAnswerData,
    GenericSurveyAnswer,
    OptionsAnswerData,
    RateAnswerData,
    SingleOptionAnswerData,
    StepsAnswerData,
    SurveyQuestionType,
    TextAnswerData,
} from '../models/surveys';

export const isTextAnswerDataValid = (data: TextAnswerData): boolean => {
    if (!data) {
        return false;
    }

    const { content } = data;

    if (!isString(content)) {
        return false;
    }

    return !!content && content != '';
};

export const isRateAnswerDataValid = (data: RateAnswerData): boolean => {
    if (!data) {
        return false;
    }

    const { content } = data;

    if (!isNumber(content)) {
        return false;
    }

    return !isUndefined(content);
};

export const isBooleanAnswerDataValid = (data: BooleanAnswerData): boolean => {
    if (!data) {
        return false;
    }

    const { content } = data;

    if (!isBoolean(content)) {
        return false;
    }

    return !isUndefined(content);
};

export const isOptionsAnswerDataValid = (data: OptionsAnswerData): boolean => {
    if (!data) {
        return false;
    }

    const { content, options } = data;

    return !!options && options.includes('Other') ? !!content && content != '' : !isEmpty(options);
};

export const isSingleOptionAnswerDataValid = (data: SingleOptionAnswerData): boolean => {
    if (!data) {
        return false;
    }

    const { content, option } = data;

    return !!option && option === 'Other' ? !!content && content != '' : !!option;
};

export const isStepsAnswerDataValid = (data: StepsAnswerData): boolean => {
    if (!data) {
        return false;
    }

    const stepsAnswers = data.content || [];

    return stepsAnswers.some(stepAnswer =>
        stepAnswer.content ? stepAnswer.content !== '' : false,
    );
};

export const isAnswerValid = (answer: GenericSurveyAnswer): boolean => {
    if (!answer) {
        return false;
    }

    if (answer.notRequired) {
        return true;
    }

    switch (answer.type) {
        case SurveyQuestionType.Text:
        case SurveyQuestionType.TextArea:
        case SurveyQuestionType.DateTime:
        case SurveyQuestionType.Select:
            return isTextAnswerDataValid(answer.data as TextAnswerData);
        case SurveyQuestionType.Rate:
            return isRateAnswerDataValid(answer.data as RateAnswerData);
        case SurveyQuestionType.Boolean:
            return isBooleanAnswerDataValid(answer.data as BooleanAnswerData);
        case SurveyQuestionType.Options:
            return isOptionsAnswerDataValid(answer.data as OptionsAnswerData);
        case SurveyQuestionType.SingleOption:
            return isSingleOptionAnswerDataValid(answer.data as SingleOptionAnswerData);
        case SurveyQuestionType.Steps:
            return isStepsAnswerDataValid(answer.data as StepsAnswerData);
        case SurveyQuestionType.Dummy:
            return true;
        default:
            return false;
    }
};

export const isEveryAnswerValid = (answers: GenericSurveyAnswer[] = []): boolean =>
    isEmpty(answers) ? false : answers.every(isAnswerValid);

export const isAnyAnswerValid = (answers: GenericSurveyAnswer[] = []): boolean =>
    isEmpty(answers) ? false : answers.some(isAnswerValid);
